const HTTP_TOO_MANY_REQUESTS = 429;
const SERVER_RESEND_COOLDOWN_CODE = 'phone_resend_cooldown';

// Bounded diagnostic reasons for failures that prevent verification. The
// vocabulary is a contract with PhoneVerificationService::VERIFIER_FAILURE_REASONS
// and with the mobile client, so it never grows to fit a provider code.
export const VERIFIER_FAILURE_REASONS = {
    'auth/invalid-app-credential': 'invalid_app_credential',
    'auth/missing-app-credential': 'invalid_app_credential',
    'auth/captcha-check-failed': 'captcha_check_failed',
    'auth/network-request-failed': 'network',
    'auth/internal-error': 'internal_error',
    'auth/too-many-requests': 'throttled',
    'auth/quota-exceeded': 'quota_exceeded',
    // The page raises this one itself. Same literal as BLOCKED in verify-phone.js.
    'blocked': 'blocked',
};

// Keep send failures distinct from errors raised while confirming a delivered code.
export const SEND_REFUSAL_CODES = [
    'auth/internal-error',
    'auth/too-many-requests',
    'auth/quota-exceeded',
];

// The server stores this in a VARCHAR(512) and truncates to the same
// length, so a longer string only wastes the request.
export const VERIFIER_FAILURE_MESSAGE_MAX = 512;

// Firebase error code to the message key the error box shows. Codes absent
// here fall back to the generic message.
export const FIREBASE_MESSAGE_KEYS = {
    'auth/invalid-phone-number': 'number',
    'auth/missing-phone-number': 'number',
    'auth/invalid-app-credential': 'security',
    'auth/missing-app-credential': 'security',
    'auth/captcha-check-failed': 'security',
    'auth/invalid-verification-code': 'code',
    'auth/missing-verification-code': 'code',
    'auth/code-expired': 'code_expired',
    'auth/network-request-failed': 'network',
    // Firebase accepted the request and refused to send to this number.
    // Retrying the same number usually fails the same way, so the message
    // offers a different one rather than a plain retry.
    'auth/internal-error': 'undeliverable',
};

const FIREBASE_CODE_PREFIX = 'auth/';

// Every Firebase code the page recognises by name. Derived rather than listed,
// so a code named in either map above can never also count as unrecognised.
const RECOGNISED_FIREBASE_CODES = Object.keys(FIREBASE_MESSAGE_KEYS)
    .concat(Object.keys(VERIFIER_FAILURE_REASONS));

/**
 * Build the body of a verifier-failure report, or return null to send none.
 *
 * `sending` separates a refused send from an error raised while confirming a
 * code the provider already delivered.
 */
export function verifierFailureReport(error, sending) {
    const code = error && error.code;
    if (typeof code !== 'string' || code === '') return null;

    const mapped = VERIFIER_FAILURE_REASONS[code];
    // firebase-auth names a server error it does not recognise by slugifying
    // the server's own message, so a refused send arrives as auth/error-code:-39
    // rather than auth/internal-error. A code the page cannot name either is
    // therefore a refusal, and reports as internal_error, because the alternative
    // is no report and no report is also no waiver. The raw code travels in the
    // message instead of widening the reason vocabulary.
    //
    // Recognised codes are excluded, not merely unmapped: auth/invalid-phone-number
    // names the number the user typed, and waiving a gate for it would hand every
    // user a way past the gate.
    const reason = mapped
        || (sending && isUnrecognisedFirebaseCode(code) ? 'internal_error' : '');
    if (!reason) return null;
    if (!sending && SEND_REFUSAL_CODES.indexOf(code) !== -1) return null;

    const values = { reason: reason };
    const message = verifierFailureMessage(error, code, Boolean(mapped));
    if (message) values.message = message;

    return values;
}

function isUnrecognisedFirebaseCode(code) {
    return code.indexOf(FIREBASE_CODE_PREFIX) === 0
        && RECOGNISED_FIREBASE_CODES.indexOf(code) === -1;
}

function verifierFailureMessage(error, code, mapped) {
    const provider = error && error.message;
    const text = typeof provider === 'string' && provider !== code ? provider : '';
    // An unmapped code is the only record of which refusal this was, so it leads
    // the detail. A mapped code already reaches the server as the reason.
    const detail = mapped ? text : [code, text].filter(Boolean).join(' ');

    return detail.slice(0, VERIFIER_FAILURE_MESSAGE_MAX);
}

export function apiFailure(response, data, fallback, defaultRetryAfter) {
    const error = data && data.error;
    const isCooldown = response.status === HTTP_TOO_MANY_REQUESTS
        && error && error.code === SERVER_RESEND_COOLDOWN_CODE;
    const retryAfter = error && error.extra && Number(error.extra.retry_after);

    return {
        code: response.status === HTTP_TOO_MANY_REQUESTS
            ? (isCooldown ? 'resend_cooldown' : 'rate_limited')
            : fallback,
        retryAfter: retryAfter > 0 ? retryAfter : defaultRetryAfter,
    };
}

export function createCooldownController({
    clearExpiry,
    clearInterval,
    defaultLabel,
    defaultSeconds,
    formatLabel,
    now,
    onRestore = () => {},
    readExpiry,
    resendButton,
    resendLabel,
    sendButton,
    setInterval,
    writeExpiry,
}) {
    let expiresAt = 0;
    let remaining = 0;
    let timer = null;
    let resendBusy = false;
    let sendBusy = false;

    function render() {
        if (sendButton) sendButton.disabled = sendBusy || remaining > 0;
        if (resendButton) resendButton.disabled = resendBusy || remaining > 0;
        if (resendLabel) {
            resendLabel.textContent = remaining > 0 ? formatLabel(remaining) : defaultLabel;
        }
    }

    function clearTimer() {
        if (timer) clearInterval(timer);
        timer = null;
    }

    function stop() {
        clearTimer();
        expiresAt = 0;
        remaining = 0;
        clearExpiry();
        render();
    }

    function tick() {
        remaining = Math.max(0, Math.ceil((expiresAt - now()) / 1000));
        if (remaining <= 0) {
            stop();
            return;
        }
        render();
    }

    return {
        get remaining() {
            return remaining;
        },
        setResendBusy(busy) {
            resendBusy = busy;
            render();
        },
        setSendBusy(busy) {
            sendBusy = busy;
            render();
        },
        start(seconds) {
            clearTimer();
            const duration = seconds > 0 ? seconds : defaultSeconds;
            expiresAt = now() + (duration * 1000);
            remaining = duration;
            writeExpiry(expiresAt);
            render();
            timer = setInterval(tick, 1000);
        },
        restore() {
            clearTimer();
            expiresAt = Number(readExpiry()) || 0;
            remaining = Math.max(0, Math.ceil((expiresAt - now()) / 1000));
            if (remaining <= 0) {
                stop();
                return;
            }
            render();
            onRestore(remaining);
            timer = setInterval(tick, 1000);
        },
        stop,
    };
}

export async function confirmWithCachedToken({
    code,
    postConfirmation,
    responseJson,
    state,
    warn = () => {},
}) {
    const sessionGeneration = state.sessionGeneration;
    const nonce = state.nonce;
    const confirmationResult = state.confirmationResult;

    function isCurrentSession() {
        return state.sessionGeneration === sessionGeneration;
    }

    if (!state.firebaseIdToken) {
        if (!state.firebaseUser) {
            const credential = await confirmationResult.confirm(code);
            if (!isCurrentSession()) return { stale: true };
            state.firebaseUser = credential.user;
        }

        const firebaseUser = state.firebaseUser;
        const firebaseIdToken = await firebaseUser.getIdToken();
        if (!isCurrentSession()) return { stale: true };
        state.firebaseIdToken = firebaseIdToken;
        try {
            if (state.auth) {
                await state.auth.signOut();
            }
        } catch (error) {
            warn(error);
        }
        state.firebaseUser = null;
    }

    const firebaseIdToken = state.firebaseIdToken;
    const response = await postConfirmation(firebaseIdToken, nonce);
    const data = await responseJson(response);
    if (!isCurrentSession()) return { data, response, stale: true };
    const verified = response.ok && data.status === 'verified';
    const terminal = !verified && response.status !== HTTP_TOO_MANY_REQUESTS && response.status < 500;

    if (verified || terminal) {
        state.firebaseIdToken = '';
        state.firebaseUser = null;
        state.confirmationResult = null;
    }

    return { data, response, stale: false, terminal, verified };
}
